import {useEffect, useRef} from 'react'
import {subscribe, unsubscribe} from '@/script/event.mjs'

export const createPlugin = (key, {extensions: install, watchers}) => {
    const extensions = doc => {
        const ext = install?.(doc)

        if (! ext) {
            return
        }

        for (const key of Object.keys(ext)) {
            const desc = Reflect.getOwnPropertyDescriptor(ext, key)

            for (const k of ['get', 'set', 'value']) {
                if ('function' === typeof desc[k]) {
                    desc[k] = desc[k].bind(doc)
                }
            }

            Reflect.defineProperty(ext, key, desc)
        }

        return {[key]: ext}
    }

    return {extensions, watchers}
}

const useInstallExtentions = (doc, extensions) => {
    const refKey2descBak = useRef(new Map)

    for (const [key, desc] of refKey2descBak.current) {
        if (desc) {
            Reflect.defineProperty(doc, key, desc)
        }
        else {
            Reflect.deleteProperty(doc, key)
        }
    }

    refKey2descBak.current = new Map

    for (const install of extensions) {
        const ext = install(doc)

        if (! ext) {
            continue
        }

        for (const key of Object.keys(ext)) {
            const desc = Reflect.getOwnPropertyDescriptor(ext, key)

            for (const k of ['get', 'set', 'value']) {
                if ('function' === typeof desc[k]) {
                    desc[k] = desc[k].bind(doc)
                }
            }

            if (! refKey2descBak.current.has(key)) {
                if (Object.hasOwn(doc, key)) {
                    refKey2descBak.current.set(
                        key,
                        Reflect.getOwnPropertyDescriptor(doc, key)
                    )
                }
                else {
                    refKey2descBak.current.set(key, null)
                }
            }

            Reflect.defineProperty(doc, key, desc)
        }
    }
}

const useInstallWatchers = (doc, watchers) => {
    useEffect(
        () => {
            const listeners = watchers
                .map(watcher => {
                    return Object.entries(watcher).map(
                        ([eventType, handler]) => [
                            eventType,
                            handler.bind(doc)
                        ]
                    )
                })
                .flat()

            for (const [eventType, listener] of listeners) {
                subscribe(doc, eventType, listener)
            }

            return () => {
                for (const [eventType, listener] of listeners) {
                    unsubscribe(doc, eventType, listener)
                }
            }
        },

        [watchers]
    )
}

export const useInstallPlugins = (doc, plugins) => {
    const extensions = []
    const watchers = []

    for (const {extensions: e, watchers: w} of plugins) {
        if (e) {
            extensions.push(e)
        }

        if (w) {
            watchers.push(w)
        }
    }

    useInstallExtentions(doc, extensions)
    useInstallWatchers(doc, watchers)
}
